import type { AnalyticsInterval, StaffAnalyticsMetric } from "@lafayette/schemas";
import type { ColumnDef } from "@tanstack/react-table";
import { type KeyboardEvent, type PointerEvent, useEffect, useId, useRef, useState } from "react";

import { DataTable } from "../../ui/data-table";
import { gridValues, niceMax } from "../charts/chart-math";
import { bucketText } from "../format";
import {
  axisText,
  type BarColumn,
  breakdownText,
  chartModel,
  chartSummary,
  valueText,
} from "./explore-model";
import { METRIC_INFO, seriesLabel } from "./metric-catalog";
import { bucketAt, bucketTitle, ZOOM_HINT, type Zoom, zoomOfBuckets } from "./zoom-math";

// The Explore chart: bars per bucket of time, stacked by the split, with the previous period as a line across each bar. SVG with palette colours,
// 1 px grid lines and no gradient. A person with a mouse drags across the bars to zoom, or clicks one bar to zoom into it. A person with a keyboard
// focuses the chart, moves with the arrow keys, extends a selection with Shift and zooms with Enter (Esc goes back one level, handled by the tab).
// A touch screen taps a bar, and the range controls of the tab replace the drag. Every number is also a table that a person can open.

const HEIGHT = 260;
const AXIS_LEFT = 48;
const AXIS_BOTTOM = 24;
const PLOT_TOP = 8;
const FALLBACK_WIDTH = 640;

// Whole class names, written out: Tailwind finds a class only when it is in the source in full. The first series is the strongest blue.
const SERIES_CLASSES = [
  { fill: "fill-primary", swatch: "bg-primary" },
  { fill: "fill-info", swatch: "bg-info" },
  { fill: "fill-brass", swatch: "bg-brass" },
  { fill: "fill-success", swatch: "bg-success" },
  { fill: "fill-neutral-dot", swatch: "bg-neutral-dot" },
] as const;

const seriesClass = (index: number) =>
  SERIES_CLASSES[index % SERIES_CLASSES.length] ?? SERIES_CLASSES[0];

function useWidth(): readonly [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.floor(entry?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

type Selection = Readonly<{ anchor: number; active: number }>;
const rangeOf = (selection: Selection): readonly [number, number] => [
  Math.min(selection.anchor, selection.active),
  Math.max(selection.anchor, selection.active),
];

function Tooltip({
  column,
  metric,
  interval,
  tz,
  left,
  width,
}: Readonly<{
  column: BarColumn;
  metric: StaffAnalyticsMetric;
  interval: AnalyticsInterval;
  tz: string;
  left: number;
  width: number;
}>) {
  const name = METRIC_INFO[metric.metric];
  const lines = breakdownText(column, metric);
  const box = 208;
  const x = Math.min(Math.max(left - box / 2, 0), Math.max(width - box, 0));
  return (
    <div
      role="status"
      style={{ left: x, width: box }}
      className="pointer-events-none absolute top-2 z-10 grid gap-1 rounded-xl bg-foreground px-3 py-2.5 text-xs leading-4 text-background shadow-lg"
    >
      <p className="m-0 text-[13px] font-semibold">
        {bucketTitle(column.t, tz, interval)}
        {column.total === null ? null : ` · ${valueText(column.total, metric.unit)}`}
      </p>
      {lines.map((line) => (
        <p key={line} className="m-0">
          {line}
        </p>
      ))}
      {column.previous === null ? null : (
        <p className="m-0">Previous period: {valueText(column.previous, metric.unit)}</p>
      )}
      {column.partial ? <p className="m-0">Not a whole {interval}: only part is counted</p> : null}
      {name.app ? <p className="m-0">Counted by the start of each visit</p> : null}
      <p className="m-0 font-semibold">{ZOOM_HINT[interval]}</p>
    </div>
  );
}

type Layout = Readonly<{
  plotWidth: number;
  slot: number;
  barWidth: number;
  scale: (value: number) => number;
}>;

function layoutOf(width: number, count: number, max: number): Layout {
  const plotWidth = Math.max(width - AXIS_LEFT, 1);
  const slot = plotWidth / Math.max(count, 1);
  const plotHeight = HEIGHT - AXIS_BOTTOM - PLOT_TOP;
  return {
    plotWidth,
    slot,
    barWidth: Math.max(1, slot * 0.72),
    scale: (value) => (max <= 0 ? 0 : (value / max) * plotHeight),
  };
}

function Bars({
  columns,
  keys,
  stacked,
  layout,
}: Readonly<{
  columns: readonly BarColumn[];
  keys: readonly string[];
  stacked: boolean;
  layout: Layout;
}>) {
  const floor = HEIGHT - AXIS_BOTTOM;
  return (
    <>
      {columns.map((column, index) => {
        const x = index * layout.slot + (layout.slot - layout.barWidth) / 2;
        return (
          <g key={column.t} className={column.partial ? "opacity-60" : undefined}>
            {column.segments.map((segment, order) => {
              const height = layout.scale(segment.y1) - layout.scale(segment.y0);
              if (height <= 0) return null;
              const part = stacked ? layout.barWidth : layout.barWidth / keys.length;
              return (
                <rect
                  key={segment.key}
                  x={stacked ? x : x + order * part}
                  y={floor - layout.scale(segment.y1)}
                  width={part}
                  height={Math.max(1, height)}
                  rx={Math.min(3, part / 2)}
                  className={seriesClass(order).fill}
                />
              );
            })}
            {column.previous === null ? null : (
              <line
                x1={x - 1}
                x2={x + layout.barWidth + 1}
                y1={floor - layout.scale(column.previous)}
                y2={floor - layout.scale(column.previous)}
                className="stroke-foreground"
                strokeWidth={2}
              />
            )}
          </g>
        );
      })}
    </>
  );
}

function Grid({
  max,
  unit,
  layout,
}: Readonly<{ max: number; unit: StaffAnalyticsMetric["unit"]; layout: Layout }>) {
  return (
    <>
      {gridValues(max).map((value) => {
        const y = HEIGHT - AXIS_BOTTOM - layout.scale(value);
        return (
          <g key={value}>
            <line
              x1={AXIS_LEFT}
              x2={AXIS_LEFT + layout.plotWidth}
              y1={y}
              y2={y}
              className="stroke-border"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={AXIS_LEFT - 8}
              y={y + 4}
              textAnchor="end"
              className="fill-muted-foreground text-[11px]"
            >
              {axisText(value, unit)}
            </text>
          </g>
        );
      })}
    </>
  );
}

type TableRow = Readonly<{
  id: string;
  when: string;
  cells: readonly string[];
  previous: string | null;
}>;

function ChartTable({
  metric,
  columns,
  keys,
  interval,
  tz,
}: Readonly<{
  metric: StaffAnalyticsMetric;
  columns: readonly BarColumn[];
  keys: readonly string[];
  interval: AnalyticsInterval;
  tz: string;
}>) {
  const info = METRIC_INFO[metric.metric];
  const rows: readonly TableRow[] = columns.map((column) => ({
    id: column.t,
    when: `${bucketText(column.t, tz, interval)}${column.partial ? " (part)" : ""}`,
    cells: column.segments.map((segment) => valueText(segment.value, metric.unit)),
    previous: column.previous === null ? null : valueText(column.previous, metric.unit),
  }));
  const defs: ColumnDef<TableRow>[] = [
    { id: "when", header: "When", accessorFn: (row) => row.when, meta: { rowHeader: true } },
    ...keys.map((key, index) => ({
      id: `series-${key}`,
      header: seriesLabel(metric.split, key),
      accessorFn: (row: TableRow) => row.cells[index],
    })),
    ...(metric.previous === null
      ? []
      : [
          {
            id: "previous",
            header: "Previous period",
            accessorFn: (row: TableRow) => row.previous,
          },
        ]),
  ];
  return (
    <details className="text-sm">
      <summary className="inline-flex min-h-11 cursor-pointer items-center font-medium text-link compact:min-h-8">
        Show the numbers of each {interval}
      </summary>
      <DataTable
        caption={info.title(interval)}
        columns={defs}
        data={rows}
        getRowId={(row) => row.id}
        stickyHeader
        maxHeightClassName="max-h-64"
      />
    </details>
  );
}

type Interaction = Readonly<{
  /** The bar under the pointer, or under the keyboard. */
  active: number | null;
  /** The bars that a drag or Shift and the arrow keys have selected, first and last, or `null`. */
  picked: readonly [number, number] | null;
  announcement: string;
  svgProps: Readonly<{
    onPointerDown: (event: PointerEvent<SVGSVGElement>) => void;
    onPointerMove: (event: PointerEvent<SVGSVGElement>) => void;
    onPointerUp: (event: PointerEvent<SVGSVGElement>) => void;
    onPointerLeave: () => void;
    onPointerCancel: () => void;
  }>;
  groupProps: Readonly<{
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
    onFocus: () => void;
    onBlur: () => void;
  }>;
}>;

/**
 * What a person does to the chart: hover, drag, click, tap and the keyboard. Every way ends in `onZoom` with the window of the buckets chosen.
 * Touch never drags (the page scrolls); a tap zooms into one bar, and the date controls replace the drag.
 */
function useChartInteraction(
  input: Readonly<{
    columns: readonly BarColumn[];
    plotWidth: number;
    bound: Zoom;
    onZoom: (zoom: Zoom) => void;
    describe: (column: BarColumn) => string;
  }>,
): Interaction {
  const { columns, plotWidth, bound, onZoom, describe } = input;
  const [hover, setHover] = useState<number | null>(null);
  const [drag, setDrag] = useState<Selection | null>(null);
  const [keyboard, setKeyboard] = useState<Selection | null>(null);
  const dragging = useRef<number | null>(null);
  const starts = columns.map((column) => column.t);

  const zoomRange = (first: number, last: number) => {
    const zoom = zoomOfBuckets(starts, first, last, bound);
    if (zoom !== null) onZoom(zoom);
  };
  const indexOf = (event: PointerEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return bucketAt(event.clientX - box.left - AXIS_LEFT, plotWidth, columns.length);
  };

  const onPointerDown = (event: PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0 && event.pointerType === "mouse") return;
    const index = indexOf(event);
    dragging.current = index;
    setDrag({ anchor: index, active: index });
    if (event.pointerType !== "touch") event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    const index = indexOf(event);
    setHover(index);
    if (dragging.current !== null && event.pointerType !== "touch") {
      setDrag({ anchor: dragging.current, active: index });
    }
  };
  const onPointerUp = (event: PointerEvent<SVGSVGElement>) => {
    const start = dragging.current;
    dragging.current = null;
    setDrag(null);
    if (start !== null) zoomRange(start, event.pointerType === "touch" ? start : indexOf(event));
  };
  const onPointerCancel = () => {
    dragging.current = null;
    setDrag(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const last = columns.length - 1;
    const current = keyboard ?? { anchor: 0, active: 0 };
    const moves: Readonly<Record<string, number>> = {
      ArrowRight: Math.min(current.active + 1, last),
      ArrowLeft: Math.max(current.active - 1, 0),
      Home: 0,
      End: last,
    };
    const next = moves[event.key];
    if (next !== undefined) {
      event.preventDefault();
      setKeyboard({ anchor: event.shiftKey ? current.anchor : next, active: next });
    } else if (event.key === "Enter") {
      event.preventDefault();
      zoomRange(...rangeOf(current));
      // The focus stays on the chart: the next Enter zooms into the first bar of the new window.
      setKeyboard({ anchor: 0, active: 0 });
    }
  };

  const active = keyboard?.active ?? hover;
  const shown = active === null ? undefined : columns[active];
  const picked = drag ?? keyboard;
  return {
    active,
    picked: picked === null ? null : rangeOf(picked),
    announcement: keyboard === null || shown === undefined ? "" : describe(shown),
    svgProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerLeave: () => setHover(null),
      onPointerCancel,
    },
    groupProps: {
      onKeyDown,
      onFocus: () => setKeyboard((current) => current ?? { anchor: 0, active: 0 }),
      onBlur: () => setKeyboard(null),
    },
  };
}

function Legend({
  metric,
  keys,
}: Readonly<{ metric: StaffAnalyticsMetric; keys: readonly string[] }>) {
  return (
    <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs text-muted-foreground">
      {keys.map((key, order) => (
        <li key={key} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={`inline-block size-2.5 rounded-sm ${seriesClass(order).swatch}`}
          />
          {seriesLabel(metric.split, key)}
        </li>
      ))}
      {metric.previous === null ? null : (
        <li className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-0.5 w-3 bg-foreground" />
          Previous period
        </li>
      )}
    </ul>
  );
}

function AxisLabels({
  columns,
  layout,
  tz,
  interval,
}: Readonly<{
  columns: readonly BarColumn[];
  layout: Layout;
  tz: string;
  interval: AnalyticsInterval;
}>) {
  const every = Math.max(
    1,
    Math.ceil(columns.length / Math.max(2, Math.floor(layout.plotWidth / 90))),
  );
  return (
    <>
      {columns.map((column, index) =>
        index % every === 0 ? (
          <text
            key={column.t}
            x={index * layout.slot + layout.slot / 2}
            y={HEIGHT - 6}
            textAnchor="middle"
            className="fill-muted-foreground text-[11px]"
          >
            {bucketText(column.t, tz, interval)}
          </text>
        ) : null,
      )}
    </>
  );
}

/** The band behind the bars that a hover or a selection covers. */
function Band({ from, to, layout }: Readonly<{ from: number; to: number; layout: Layout }>) {
  return (
    <rect
      x={from * layout.slot}
      y={PLOT_TOP}
      width={(to - from + 1) * layout.slot}
      height={HEIGHT - AXIS_BOTTOM - PLOT_TOP}
      className="fill-accent"
    />
  );
}

export function ExploreChart({
  metric,
  interval,
  tz,
  bound,
  onZoom,
}: Readonly<{
  metric: StaffAnalyticsMetric;
  interval: AnalyticsInterval;
  tz: string;
  /** The window of the series: the last bucket ends where it ends. */
  bound: Zoom;
  onZoom: (zoom: Zoom) => void;
}>) {
  const model = chartModel(metric);
  const { columns, keys, stacked } = model;
  const max = niceMax(model.max);
  const [frame, width] = useWidth();
  const layout = layoutOf(width, columns.length, max);
  const hintId = useId();
  const interaction = useChartInteraction({
    columns,
    plotWidth: layout.plotWidth,
    bound,
    onZoom,
    describe: (column) =>
      column.total === null
        ? bucketTitle(column.t, tz, interval)
        : `${bucketTitle(column.t, tz, interval)}: ${valueText(column.total, metric.unit)}`,
  });
  const { active, picked } = interaction;
  const shown = active === null ? undefined : columns[active];
  const band = picked ?? (active === null ? null : ([active, active] as const));
  return (
    <div className="grid gap-3">
      <div ref={frame} className="relative min-w-0 overflow-hidden">
        <figure
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the chart takes the arrow keys, so it must be reachable by keyboard
          tabIndex={0}
          aria-roledescription="chart"
          aria-label={`${METRIC_INFO[metric.metric].title(interval)}. ${chartSummary(metric.metric, metric, interval)}`}
          aria-describedby={hintId}
          {...interaction.groupProps}
          className="m-0 rounded-md outline-offset-2"
        >
          <svg
            width={width}
            height={HEIGHT}
            viewBox={`0 0 ${width} ${HEIGHT}`}
            aria-hidden="true"
            focusable="false"
            style={{ touchAction: "pan-y" }}
            className="block cursor-crosshair select-none"
            {...interaction.svgProps}
          >
            <Grid max={max} unit={metric.unit} layout={layout} />
            <g transform={`translate(${AXIS_LEFT} 0)`}>
              {band === null ? null : <Band from={band[0]} to={band[1]} layout={layout} />}
              <Bars columns={columns} keys={keys} stacked={stacked} layout={layout} />
              <AxisLabels columns={columns} layout={layout} tz={tz} interval={interval} />
            </g>
          </svg>
        </figure>
        {shown === undefined || active === null ? null : (
          <Tooltip
            column={shown}
            metric={metric}
            interval={interval}
            tz={tz}
            left={AXIS_LEFT + active * layout.slot + layout.slot / 2}
            width={width}
          />
        )}
        <p className="sr-only" aria-live="polite">
          {interaction.announcement}
        </p>
      </div>
      <p id={hintId} className="m-0 text-xs text-muted-foreground">
        Drag across the bars to zoom, or click one bar. With the keyboard: arrow keys move, Shift
        and an arrow key select, Enter zooms, Esc goes back. On a touch screen, tap a bar or use
        Choose dates.
      </p>
      <Legend metric={metric} keys={keys} />
      <ChartTable metric={metric} columns={columns} keys={keys} interval={interval} tz={tz} />
    </div>
  );
}
